import { HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Cùng bộ từ với `huongDelta` ở `ui/dinhDang.ts` và với `StatCard`.
 *
 * Bước 1 đặt `'ngang'` cho mức không đổi, nhưng `huongDelta` trả `'phang'` nên
 * mọi trang muốn dùng nó đều phải tự dịch — một chỗ dịch sai là mũi tên chỉ
 * ngược chiều.
 */
export type HuongChiSo = 'tang' | 'giam' | 'phang';

export interface OChiSo {
  /**
   * Nhãn. **Ghi rõ khoảng thời gian** khi số chỉ đúng cho một kỳ:
   * `Thực nhận · tháng 10`, không chỉ `Thực nhận`.
   */
  nhan: string;
  giaTri: ReactNode;
  /** Dòng phụ: chú thích ngắn, hoặc để trống. */
  phu?: ReactNode;
  /**
   * Đoạn giải thích dài, hiện trong tooltip của icon (?) cạnh nhãn.
   *
   * Dùng khi một ô **đếm theo luật không hiển nhiên** — ví dụ "Kênh kiếm tiền"
   * của trang Tổng quan: tử số theo tình trạng YPP, dòng phụ còn có một số đếm
   * theo doanh thu thực. Không nói ra thì người đọc tự suy, và suy sai.
   */
  giaiThich?: string;
  /**
   * Tăng/giảm so với kỳ trước. Để trống khi không có kỳ trước để so.
   *
   * `moTa` là câu đầy đủ cho tooltip (`Giảm 13,8% so 01/09 – 26/09/2026`). Dùng
   * khi `text` chỉ có con số: một mũi tên đỏ với "13,8%" không nói ra mình so
   * với khoảng nào, mà kỳ chưa trọn thì khoảng đó **không** phải cả tháng
   * trước.
   */
  delta?: { huong: HuongChiSo; text: string; moTa?: string };
}

export interface StatStripProps {
  o: OChiSo[];
  /** Nhãn cho trình đọc màn hình, ví dụ `Chỉ số tháng 10`. */
  'aria-label'?: string;
  /**
   * Kỳ đang xem **không có số liệu nào** (ngoài mốc dữ liệu): mọi ô hiện `—`,
   * không hiện delta.
   *
   * Dùng cho link cũ kiểu `?ky=2017` và link của người có phạm vi rộng hơn —
   * xem `kyNgoaiMocDuLieu` ở `lib/kyTrang.ts`. API trả **0** cho những kỳ đó,
   * mà `0,00 $` đọc ra là "doanh thu tụt về không", không phải "không có số".
   */
  kyRong?: boolean;
}

/**
 * Màu và ký hiệu của delta — **export** vì khối khác cũng in delta của **cùng
 * một chỉ số**.
 *
 * Tiêu đề biểu đồ "Lượt xem toàn hệ thống" in lại đúng con số và phần trăm của
 * ô "Lượt xem" trong dải. Nếu nó tự khai một bảng ký hiệu thứ hai thì hai chỗ
 * trôi khỏi nhau (bản trước 26/10/2026: ô ghi `▲ 2,1%`, biểu đồ ghi `↑ 5,0%` —
 * hai mũi tên khác nhau cho hai phép so khác nhau, không chỗ nào nói ra).
 */
export const MAU_DELTA: Record<HuongChiSo, string> = {
  tang: 'text-tang',
  giam: 'text-giam',
  phang: 'text-chu-phu',
};

export const DAU_DELTA: Record<HuongChiSo, string> = {
  tang: '▲',
  giam: '▼',
  phang: '—',
};

/**
 * Số cột theo **bề ngang của chính dải**, không theo khung nhìn.
 *
 * Một ô cần khoảng **165px chỗ trống** (197px kể cả padding 16px hai bên) thì
 * nhãn tiếng Việt mới không xuống dòng từng từ và số tiền mới không gãy đôi.
 * Nhân lên theo số ô là ra ngưỡng của từng nấc:
 *
 * | Số ô | 2 cột | 3 cột | đủ cột |
 * |---|---|---|---|
 * | 3 | < 600 | ≥ 600 | |
 * | 4 | < 788 | | ≥ 788 |
 * | 5 | < 985 | | ≥ 985 |
 * | 6 | < 600 | ≥ 600 | ≥ 1182 |
 *
 * Nấc 3 cột **chỉ dành cho số ô chia hết cho 3** (3 và 6): 5 ô xếp 3 cột thì
 * hàng dưới có một ô trống, trông như thiếu mất một chỉ số.
 *
 * Lớp phải là chuỗi **tĩnh** — Tailwind quét mã nguồn, lớp ghép động
 * (`@min-[${x}px]:grid-cols-${n}`) không có trong CSS đã build.
 */
const COT_THEO_SO_O: Record<number, string> = {
  1: '',
  2: '',
  3: '@min-[600px]/dai:grid-cols-3',
  4: '@min-[788px]/dai:grid-cols-4',
  5: '@min-[985px]/dai:grid-cols-5',
  6: '@min-[600px]/dai:grid-cols-3 @min-[1182px]/dai:grid-cols-6',
};

/**
 * Ô cuối của dải **lẻ ô** chiếm cả hàng khi còn 2 cột, và thôi chiếm khi đã đủ
 * cột cho nó. Ngưỡng phải trùng ngưỡng ở `COT_THEO_SO_O`.
 */
const O_CUOI_LE: Record<number, string> = {
  1: 'col-span-2',
  3: 'col-span-2 @min-[600px]/dai:col-span-1',
  5: 'col-span-2 @min-[985px]/dai:col-span-1',
};

/**
 * Dải chỉ số trong **một** thẻ, các ô ngăn nhau bằng đường kẻ mảnh.
 *
 * Thay bốn `StatCard` rời: bốn thẻ bốn viền bốn khoảng trắng làm đầu trang trông
 * như bốn thứ không liên quan, trong khi chúng là bốn mặt của **cùng một kỳ**.
 * Một thẻ, kẻ dọc, không icon trang trí — icon màu cạnh mỗi số chỉ thêm việc
 * cho mắt mà không thêm thông tin nào.
 *
 * Số cột đọc theo **container query** (`@container/dai`), không theo khung
 * nhìn: dải nằm trong vùng nội dung, mà vùng nội dung hẹp hơn khung nhìn đúng
 * bằng bề ngang thanh bên. Bản cũ dùng `lg:grid-cols-6`, nên trên laptop
 * 1280px (vùng nội dung 992px) sáu ô ra sáu cột rộng 165px — trừ padding còn
 * 133px cho chữ, và "Doanh thu gộp YouTube" đọc thành bốn dòng.
 */
export function StatStrip({ o, 'aria-label': nhan, kyRong = false }: StatStripProps) {
  return (
    <section
      aria-label={nhan}
      data-khoi="chi-so"
      className="@container/dai overflow-hidden rounded-the border border-vien bg-be-mat"
    >
      {/*
        Kẻ ngăn bằng viền **trên + trái của từng ô**, rồi kéo cả lưới lên và
        sang trái 1px để `overflow-hidden` của thẻ nuốt hàng kẻ ngoài cùng.

        Bản cũ tính viền theo **chỉ số ô** (`i % 2`, `i >= 2`), đúng cho đúng
        hai cột; nay số cột đổi theo bề ngang nên không chỉ số nào biết mình
        đang ở đầu hàng hay không. Cách này không cần biết.
      */}
      <div className={`-mt-px -ml-px grid grid-cols-2 ${COT_THEO_SO_O[o.length] ?? ''}`}>
        {o.map((x, i) => (
          <div
            key={x.nhan}
            data-o=""
            className={`border-t border-l border-vien-trong px-4 py-3.5 ${
              i === o.length - 1 ? (O_CUOI_LE[o.length] ?? '') : ''
            }`}
          >
            <p data-nhan="" className="flex items-start gap-1 text-phu font-medium text-chu-phu">
              {/*
                Nhãn **xuống dòng tối đa hai dòng**, không cắt bằng dấu ba chấm.
                Nhãn nay luôn mang kỳ của ô ("Công ty thực nhận · 28 ngày", xem
                mục 10 của docs/ui.md), mà `truncate` ăn đúng cái đuôi đó: trên
                laptop 1280 (vùng nội dung 992px, năm ô) dải Tổng quan đọc thành
                "Công ty thực nhận · 28 n…" — tức là mất đúng phần vừa thêm vào.
                `line-clamp-2` giữ trần hai dòng nên không quay lại cảnh chữ
                xuống dòng từng từ; `title` để chuột rê vẫn đọc được cả nhãn.
              */}
              <span className="line-clamp-2 [overflow-wrap:anywhere]" title={x.nhan}>
                {x.nhan}
              </span>
              {x.giaiThich !== undefined && (
                <button
                  type="button"
                  // Nút chỉ có icon: phải có nhãn cho trình đọc màn hình.
                  aria-label={`Giải thích: ${x.nhan}`}
                  title={x.giaiThich}
                  className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-chu-phu hover:bg-neutral-nen hover:text-chu"
                >
                  <HelpCircle size={13} aria-hidden="true" />
                </button>
              )}
            </p>
            {/*
              Số **không xuống dòng** (`.so-lieu` trong `styles.css`), nên ô
              hẹp phải giảm **cỡ số** chứ không gãy chữ: dải 2 cột ở khổ 390
              chỉ còn ~147px cho chữ, mà "1.809,00 US$" ở 21px đậm cần ~150px.
              Dưới 560px bề ngang dải thì số về 18px — vừa đủ cho một con số
              tiền bảy chữ số, và vẫn là chữ to nhất trong ô.
            */}
            <p
              data-gia-tri=""
              className="so-lieu mt-1 text-[18px] leading-[1.2] font-bold text-chu @min-[560px]/dai:text-so-lon"
            >
              {kyRong ? '—' : x.giaTri}
            </p>
            {!kyRong && (x.delta ?? x.phu) && (
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-phu">
                {x.delta && (
                  <span
                    className={`font-semibold ${MAU_DELTA[x.delta.huong]}`}
                    {...(x.delta.moTa === undefined ? {} : { title: x.delta.moTa })}
                  >
                    {DAU_DELTA[x.delta.huong]} {x.delta.text}
                  </span>
                )}
                {x.phu && <span className="text-chu-phu">{x.phu}</span>}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
